import { doc, setDoc, Timestamp } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { FREE_MOCK_FORM } from '../constants/mockForms';
import { SESSION_QUESTION_TARGET } from '../constants/product';
import {
  FREE_TRIAL_QUESTIONS,
  FREE_TRIAL_KEY,
} from '../constants/pricing';
import { levelHasFreeMock, normalizeLevel } from '../constants/levels';
import {
  freeMockKeys,
  getLocalFreeMockUsedForLevel,
  hasUsedFreeMockForLevel,
  isComplimentaryMockForm,
} from './freeMock';

export {
  PASS_PRICE_USD,
  ANCHOR_PRICE_USD,
  PASS_ACCESS_DAYS,
  PASS_LABEL,
  FREE_SESSION_KEY,
  FREE_SESSION_KEY_L2,
  FREE_SESSION_KEY_L3,
  FREE_TRIAL_QUESTIONS,
  FREE_TRIAL_KEY,
  FOUNDING_HEADLINE,
  FOUNDING_SUBHEAD,
  FOUNDING_BADGE,
  ANCHOR_COPY,
  CHECKOUT_CTA,
  SAVINGS_USD,
  VALUE_BULLETS,
  TRUST_BULLETS,
  FOUNDING_OFFER_END,
  FOUNDING_OFFER_END_LABEL,
  FOUNDING_OFFER_PUBLIC_LINE,
  FOUNDING_OFFER_UNTIL_LINE,
  formatExamUrgency,
  getFoundingOfferState,
  currentPassPriceUsd,
  foundingCountdownLabel,
  foundingHeadline,
  foundingOfferPublicLine,
  foundingPriceSentence,
  foundingTierStepLine,
  checkoutCta,
  unlockMocksFoundingCta,
  getDynamicPricing,
} from '../constants/pricing';

export {
  freeMockFormId,
  freeMockKeys,
  freeUsedFromAccess,
  getLocalFreeMockUsedForLevel,
  hasUsedFreeMockForLevel,
  isComplimentaryMockForm,
} from './freeMock';

/**
 * Normalize Firestore `paidUntil` (YYYY-MM-DD string, Timestamp, Date, or seconds map)
 * to a calendar date string. Empty string when unparseable.
 */
export function normalizePaidUntil(paidUntil) {
  if (!paidUntil) return '';
  if (typeof paidUntil === 'string') {
    const trimmed = paidUntil.trim().toLowerCase();
    if (trimmed === 'lifetime' || trimmed === 'deferred') return trimmed;
    const slice = paidUntil.trim().slice(0, 10);
    return /^\d{4}-\d{2}-\d{2}$/.test(slice) ? slice : '';
  }
  if (typeof paidUntil.toDate === 'function') {
    try {
      return paidUntil.toDate().toISOString().slice(0, 10);
    } catch {
      return '';
    }
  }
  if (typeof paidUntil.seconds === 'number') {
    return new Date(paidUntil.seconds * 1000).toISOString().slice(0, 10);
  }
  if (paidUntil instanceof Date && !Number.isNaN(paidUntil.getTime())) {
    return paidUntil.toISOString().slice(0, 10);
  }
  if (typeof paidUntil.toISOString === 'function') {
    try {
      return paidUntil.toISOString().slice(0, 10);
    } catch {
      return '';
    }
  }
  return '';
}

export function isPassActive(paidUntil) {
  const raw = normalizePaidUntil(paidUntil);
  if (!raw) return false;
  if (raw === 'lifetime') return true;
  const end = new Date(`${raw}T23:59:59.000Z`);
  return !Number.isNaN(end.getTime()) && end.getTime() >= Date.now();
}

/** Device-only: complimentary L1 Mock 1 already completed (or legacy 10-question trial exhausted). */
export function getLocalFreeMockUsed() {
  return getLocalFreeMockUsedForLevel('l1');
}

/** @deprecated alias — use getLocalFreeMockUsed */
export function getFreeTrialUsedCount() {
  return getLocalFreeMockUsed() ? 1 : 0;
}

/** Whether complimentary Mock 1 has been completed (chrome/analytics — not a start block). */
export function hasUsedFreeMock(profileFreeSessionUsed = false, user = null, profileFreeMockCompletedAt = null) {
  if (user) {
    return Boolean(profileFreeSessionUsed || profileFreeMockCompletedAt);
  }
  if (profileFreeSessionUsed || profileFreeMockCompletedAt) return true;
  return getLocalFreeMockUsed();
}

export function freeMockAvailable(profileFreeSessionUsed = false, user = null, profileFreeMockCompletedAt = null) {
  return !hasUsedFreeMock(profileFreeSessionUsed, user, profileFreeMockCompletedAt);
}

/** Merge device + profile — always read localStorage only for guests. */
export function effectiveFreeTrialUsed(
  profileFreeTrialUsed = 0,
  profileFreeSessionUsed = false,
  user = null,
  profileFreeMockCompletedAt = null
) {
  if (user) {
    return (profileFreeSessionUsed || profileFreeMockCompletedAt) ? 1 : 0;
  }
  if (hasUsedFreeMock(profileFreeSessionUsed, user, profileFreeMockCompletedAt)) return 1;
  if (profileTrialUsedCount(profileFreeTrialUsed, profileFreeSessionUsed || Boolean(profileFreeMockCompletedAt)) >= 1) return 1;
  return 0;
}

/** Profile-only trial count (admin KPIs — no localStorage). */
export function profileTrialUsedCount(freeTrialUsed = 0, freeSessionUsed = false) {
  if (freeSessionUsed) return 1;
  if (Number(freeTrialUsed) >= FREE_TRIAL_QUESTIONS) return 1;
  return 0;
}

/** Profile-only trial exhausted (admin KPIs — no localStorage). */
export function profileTrialExhausted(freeTrialUsed = 0, freeSessionUsed = false) {
  return profileTrialUsedCount(freeTrialUsed, freeSessionUsed) >= 1;
}

export function freeTrialExhausted(profileFreeTrialUsed = 0, profileFreeSessionUsed = false, user = null, profileFreeMockCompletedAt = null) {
  return effectiveFreeTrialUsed(profileFreeTrialUsed, profileFreeSessionUsed, user, profileFreeMockCompletedAt) >= 1;
}

/** 1 before first complimentary Mock 1 completion on this device/profile, else 0 (chrome only). */
export function getFreeTrialRemaining(profileFreeTrialUsed = 0, profileFreeSessionUsed = false, user = null, profileFreeMockCompletedAt = null) {
  if (user) {
    return (profileFreeSessionUsed || profileFreeMockCompletedAt) ? 0 : 1;
  }
  return freeMockAvailable(profileFreeSessionUsed, user, profileFreeMockCompletedAt) &&
    !profileTrialExhausted(profileFreeTrialUsed, profileFreeSessionUsed || Boolean(profileFreeMockCompletedAt))
    ? 1
    : 0;
}

export async function recordFreeMockCompleted(targetUid, level = 'l1') {
  const keys = freeMockKeys(level);
  const uid = targetUid || auth.currentUser?.uid;
  if (uid) {
    try {
      await setDoc(
        doc(db, 'userProfiles', uid),
        {
          [keys.usedField]: true,
          [keys.completedAtField]: Timestamp.now(),
        },
        { merge: true }
      );
    } catch (err) {
      console.error('Error saving freeMockCompleted to Firestore:', err);
    }
  }
  try {
    window.localStorage.setItem(keys.localKey, '1');
    if (keys.level === 'l1') {
      window.localStorage.setItem(FREE_TRIAL_KEY, String(FREE_TRIAL_QUESTIONS));
    }
    window.dispatchEvent(new Event('cfa-free-trial-used'));
    window.dispatchEvent(new Event('cfa-free-session-used'));
  } catch {
    /* ignore */
  }
}

/** @deprecated per-question trial — no-op under one-free-mock model */
export function recordFreeTrialQuestion() {
  recordFreeMockCompleted(undefined, 'l1');
  return getLocalFreeMockUsed() ? 1 : 0;
}

/** @deprecated use hasUsedFreeMock */
export function hasUsedFreeSession() {
  return hasUsedFreeMock();
}

/** @deprecated use hasUsedFreeMock */
export function effectiveFreeUsed(profileFreeSessionUsed = false) {
  return hasUsedFreeMock(profileFreeSessionUsed);
}

/** @deprecated use recordFreeMockCompleted */
export function markFreeSessionUsed(uid) {
  recordFreeMockCompleted(uid);
}

/**
 * Whether explanations / answer keys unlock.
 * Pass `level` ('l1'|'l2'|'l3') to scope to that product — L2-only buyers must not
 * unlock L1 free-mock keys (and vice versa). Omit `level` for “any paid practice”
 * chrome (e.g. Progress member hub).
 */
export function hasPaidPracticeAccess({
  paid,
  paidL2 = false,
  paidL3 = false,
  hasPackage = false,
  isAdmin,
  monetizationEnabled,
  level = null,
}) {
  if (!monetizationEnabled) return true;
  if (isAdmin || hasPackage) return true;
  const lv = level != null && level !== '' ? String(level).toLowerCase() : null;
  if (lv === 'l2') return Boolean(paidL2);
  if (lv === 'l3') return Boolean(paidL3);
  if (lv === 'l1') return Boolean(paid);
  return Boolean(paid || paidL2 || paidL3);
}

export function canStartQuiz({
  paid,
  isAdmin,
  freeTrialUsedCount = 0,
  freeUsed,
  freeSessionUsed = false,
  freeMockCompletedAt = null,
  monetizationEnabled,
  isSession = false,
  mockFormId = FREE_MOCK_FORM,
  isReview = false,
  level = 'l1',
}) {
  if (!monetizationEnabled) return true;
  if (isAdmin || paid) return true;
  const normalizedLevel = normalizeLevel(level);
  if (!levelHasFreeMock(normalizedLevel)) return false;
  if (isReview) return false;
  // CFA-7: complimentary Mock 1 is always free (score-only). freeUsed / freeSessionUsed
  // still tracked for chrome + analytics but must not block restarts.
  if (!isSession || !isComplimentaryMockForm(normalizedLevel, mockFormId)) return false;
  return true;
}

/** Complimentary Mock 1 completed — purchase unlocks Mocks 2–4 + explanations (Mock 1 still startable). */
export function isTrialExhausted({ monetizationEnabled, paid, isAdmin, freeUsed }) {
  return !!monetizationEnabled && !paid && !isAdmin && !!freeUsed;
}

/** Mock picker — unpaid users always reach Mock 1 (score-only); Mocks 2–4 gated in UI. */
export function mockSessionHref(_access) {
  return '/quiz/session';
}

/** Whether the client may load practice items for this quiz mode. */
export function canLoadQuestionBank(params) {
  return canStartQuiz(params);
}

export function getPracticeQuestionLimit({
  monetizationEnabled,
  paid,
  isAdmin,
  freeTrialUsedCount = 0,
  freeUsed,
  freeSessionUsed = false,
  freeMockCompletedAt = null,
  isSession = false,
  mockFormId = FREE_MOCK_FORM,
  level = 'l1',
}) {
  if (!monetizationEnabled || paid || isAdmin) {
    return isSession ? SESSION_QUESTION_TARGET : Number.POSITIVE_INFINITY;
  }
  const allowed = canStartQuiz({
    monetizationEnabled,
    paid,
    isAdmin,
    freeTrialUsedCount,
    freeUsed,
    freeSessionUsed,
    freeMockCompletedAt,
    isSession,
    mockFormId,
    level,
  });
  return allowed && isSession ? SESSION_QUESTION_TARGET : 0;
}

export function getQuizAccess({
  isSession,
  isReview,
  paid,
  isAdmin,
  freeTrialUsedCount = 0,
  freeUsed,
  freeSessionUsed = false,
  freeMockCompletedAt = null,
  monetizationEnabled,
  mockFormId = FREE_MOCK_FORM,
  level = 'l1',
}) {
  const normalizedLevel = normalizeLevel(level);
  const complimentary = isComplimentaryMockForm(normalizedLevel, mockFormId);
  let trialUsed = false;
  if (normalizedLevel === 'l1') {
    trialUsed =
      freeUsed === true ||
      freeTrialUsedCount >= 1 ||
      Boolean(freeSessionUsed) ||
      Boolean(freeMockCompletedAt) ||
      (freeUsed === undefined && hasUsedFreeMock(freeSessionUsed, null, freeMockCompletedAt));
  } else if (freeUsed === true) {
    trialUsed = true;
  } else if (freeUsed === false) {
    trialUsed = false;
  } else {
    trialUsed = hasUsedFreeMockForLevel(normalizedLevel, {
      [freeMockKeys(normalizedLevel).usedField]: freeSessionUsed,
      [freeMockKeys(normalizedLevel).completedAtField]: freeMockCompletedAt,
    });
  }
  const allowed = canStartQuiz({
    monetizationEnabled,
    paid,
    isAdmin,
    freeTrialUsedCount,
    freeUsed: trialUsed,
    freeSessionUsed,
    freeMockCompletedAt,
    isSession,
    mockFormId,
    isReview,
    level: normalizedLevel,
  });
  // freeMockAvailable / freeTrialRemaining remain chrome signals (has sat free mock),
  // not start blockers — see canStartQuiz CFA-7.
  const bankAllowed = allowed;
  let reason = 'session';
  if (isReview) reason = 'review';
  else if (!isSession) reason = 'topic';
  else if (!complimentary) reason = 'session';
  return {
    allowed,
    bankAllowed,
    reason,
    freeMockAvailable: !trialUsed,
    freeTrialRemaining: trialUsed ? 0 : 1,
  };
}

/** CFA-130 client flag (build time). Defaults OFF; only the exact value 'on' enables. */
export function guestResultsCheckoutEnabled(env = process.env) {
  return String(env.REACT_APP_GUEST_RESULTS_CHECKOUT || '').trim().toLowerCase() === 'on';
}

export async function startCheckout({
  termsAccepted = false,
  source = 'card',
  placement = 'unknown',
  cancelPath = '',
  sku = '',
  expectedPriceUsd = null,
  friendCreditUsd = 0,
  referralCode = '',
  // CFA-130: account-less checkout from guest Mock 1 results (server flag-gated).
  guestResults = false,
  sittingId = '',
} = {}) {
  if (!termsAccepted) {
    throw new Error('Please accept the Terms of Use to continue.');
  }
  const user = auth.currentUser;
  const guestPath = !user && guestResults === true && guestResultsCheckoutEnabled();
  if (!user && !guestPath) {
    throw new Error('Sign in before purchasing so access and your receipt attach to your account.');
  }
  const { getCheckoutAttribution } = await import('./acquisition');
  const attribution = getCheckoutAttribution();
  const { trackCheckoutFunnel } = await import('./checkoutTracking');
  trackCheckoutFunnel('checkout_started', {
    source,
    placement,
    sku: sku || undefined,
    guest: !user,
    ...attribution,
  });
  const headers = { 'Content-Type': 'application/json' };
  if (user) {
    const token = await user.getIdToken();
    headers.Authorization = `Bearer ${token}`;
  }
  const fallbackCancel = `${window.location.pathname}${window.location.search}` || '/pricing';
  const cancel =
    typeof cancelPath === 'string' && cancelPath.startsWith('/') && !cancelPath.startsWith('//')
      ? cancelPath.slice(0, 500)
      : fallbackCancel;
  const res = await fetch('/.netlify/functions/create-checkout', {
    method: 'POST',
    headers,
    body: JSON.stringify({
      mode: 'hosted',
      termsAccepted: termsAccepted ? '1' : '0',
      cancelPath: cancel,
      attribution: { ...attribution, ...(referralCode ? { referralCode } : {}) },
      // Button attribution for Stripe Session metadata (server allowlists; drops unknowns).
      placement: String(placement || 'unknown').slice(0, 40),
      source: String(source || 'card').slice(0, 40),
      ...(sku ? { sku } : {}),
      // The lifetime price the buyer saw; the server refuses (409) if it would charge another.
      ...(Number.isFinite(expectedPriceUsd) ? { expectedPriceUsd } : {}),
      ...(friendCreditUsd > 0 ? { friendCreditUsd } : {}),
      ...(guestPath && sittingId ? { sittingId: String(sittingId).slice(0, 64) } : {}),
    }),
  });
  const body = await res.json().catch(() => ({}));
  if (res.status === 409 && body.code === 'price_changed') {
    const err = new Error(
      body.error || 'The price for this plan changed. Review it and choose again.'
    );
    err.code = 'price_changed';
    err.priceUsd = body.priceUsd;
    throw err;
  }
  if (!res.ok || !body.url) {
    throw new Error(
      body.error ||
        'Secure checkout could not be opened. Please try again or contact billing@cfamockexam.com.'
    );
  }
  window.location.assign(body.url);
}

/** Create PaymentIntent for device wallet checkout (call on purchase click only). */
export async function createWalletIntent({ termsAccepted = false, sku = '' } = {}) {
  if (!termsAccepted) {
    throw new Error('Please accept the Terms of Use to continue.');
  }
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Sign in before purchasing so access attaches to your account.');
  }
  const { getCheckoutAttribution } = await import('./acquisition');
  const attribution = getCheckoutAttribution();
  const headers = { 'Content-Type': 'application/json' };
  if (user) {
    const token = await user.getIdToken();
    headers.Authorization = `Bearer ${token}`;
  }
  const res = await fetch('/.netlify/functions/create-wallet-intent', {
    method: 'POST',
    headers,
    body: JSON.stringify({
      termsAccepted: termsAccepted ? '1' : '0',
      attribution,
      ...(sku ? { sku } : {}),
    }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || !body.clientSecret) {
    throw new Error(
      body.error ||
        'Secure checkout could not be prepared. Please try again or contact billing@cfamockexam.com.'
    );
  }
  return body;
}

/** Attach guest billing email to a wallet PaymentIntent before confirmation. */
export async function updateWalletIntent({ paymentIntentId, buyerEmail }) {
  const res = await fetch('/.netlify/functions/update-wallet-intent', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ paymentIntentId, buyerEmail }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(body.error || 'Could not save billing email.');
  }
  return body;
}

export async function claimPass(sessionId, paymentIntentId) {
  const user = auth.currentUser;
  if (!user || (!sessionId && !paymentIntentId)) return null;
  const token = await user.getIdToken();
  const res = await fetch('/.netlify/functions/claim-pass', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ sessionId: sessionId || '', paymentIntentId: paymentIntentId || '' }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(
      body.error ||
        'We could not activate this purchase. Please try again or contact billing@cfamockexam.com with your receipt.'
    );
  }
  if (!body.stamped) {
    throw new Error(
      'Purchase activation is temporarily unavailable. Contact billing@cfamockexam.com with your receipt.'
    );
  }
  return body;
}

/** Admin-only: verify a Stripe charge/PI/session and stamp paidUntil. */
export async function repairPass({ uid = '', email = '', stripeRef = '' } = {}) {
  const user = auth.currentUser;
  if (!user) throw new Error('Sign in as admin first.');
  const token = await user.getIdToken();
  const res = await fetch('/.netlify/functions/admin-repair-pass', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ uid, email, stripeRef }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(body.error || 'Could not verify Stripe payment or grant the pass.');
  }
  return body;
}

/** Guest checkout: verify payment, grant pass, return Firebase custom token. */
export async function completeGuestPurchase({ sessionId = '', paymentIntentId = '' } = {}) {
  const res = await fetch('/.netlify/functions/complete-guest-purchase', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      sessionId: sessionId || '',
      paymentIntentId: paymentIntentId || '',
    }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok && res.status !== 202) {
    throw new Error(
      body.error ||
        body.message ||
        'We could not open your account. Sign in with your receipt email or contact billing@cfamockexam.com.'
    );
  }
  return body;
}

export async function fetchBillingPortalUrl() {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Sign in to open billing.');
  }
  const token = await user.getIdToken();
  const res = await fetch('/.netlify/functions/billing-portal', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || !body.url) {
    throw new Error(body.error || 'Could not open billing');
  }
  return body;
}

/** Schedule cancel_at_period_end with a required Stripe cancellation reason (CFA-32). */
export async function cancelMonthlySubscription({
  feedback,
  comment = '',
  subscriptionId = '',
  level = '',
} = {}) {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Sign in to cancel your subscription.');
  }
  const token = await user.getIdToken();
  const res = await fetch('/.netlify/functions/cancel-subscription', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      feedback,
      comment,
      ...(subscriptionId ? { subscriptionId } : {}),
      ...(level ? { level } : {}),
    }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || !body.ok) {
    throw new Error(body.error || 'Could not cancel subscription');
  }
  return body;
}

export async function openBillingPortal() {
  const user = auth.currentUser;
  if (!user) {
    const next = encodeURIComponent('/billing');
    window.location.assign(`/login?next=${next}`);
    return null;
  }
  const body = await fetchBillingPortalUrl();
  window.location.href = body.url;
  return body;
}
